import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { TooltipDirective } from '@cloudfactorydk/ng2-tooltip-directive';
import { ModalController } from "@ionic/angular";
import { Components } from "@ionic/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { cloneDeep, fill } from "lodash-es";
import { AnimationOptions } from 'ngx-lottie';
import { Subscription } from 'rxjs';
import { takeWhile } from "rxjs/operators";
import { Finishing, PageOrientation, PageSize, PageTurn, PrintColor, Settings } from 'src/app/shared-lib/core/settings';
import { _PrintFile, PrintFile, PrintFileStatus, PrintFileSubStatus, PrintingGroup, ProductService } from 'src/app/shared-lib/domains/product';
import { getThumbFile, UploadService } from 'src/app/shared-lib/domains/upload';
import { BlankSheetsModalComponent } from "../../components/blank-sheets-modal/blank-sheets-modal.component";
import { CoverSettingsComponent } from "../../components/cover-settings/cover-settings.component";
import {
    IndividualBindingOptionsModalComponent
} from "../../components/individual-binding-options-modal/individual-binding-options-modal.component";
import {
    IndividualSettingsComponent
} from "../../components/individual-settings/individual-settings.component";
import {
    InfoDespaginationModalComponent
} from "../../components/info-despagination-modal/info-despagination-modal.component";
import { AppService } from "../../shared/app.service";
import { CartService } from "../../shared/cart.service";
import { FileService } from "../../shared/file.service";
import { UtilsService } from "../../shared/services/utils.service";
import { LaminationOptionsComponent } from "../lamination-options/lamination-options.component";
import IonPopover = Components.IonPopover;

interface File {
  lastModified: number;
  name: string;
  size: number;
  type: string;
}

@UntilDestroy()
@Component({
  selector: "app-file",
  templateUrl: "./file.component.html",
  styleUrls: ["./file.component.scss"],
  standalone: false
})
export class FileComponent implements OnInit, OnChanges, OnDestroy {
  Finishing = Finishing;
  PageOrientation = PageOrientation;
  PageTurn = PageTurn;
  PrintColor = PrintColor;

  @ViewChild('aliasInput') aliasInput: ElementRef;
  @ViewChild('editTooltip', { static: false }) editTooltip: TooltipDirective;
  @ViewChild('despaginationIcon') despaginationIcon: any;
  @ViewChild('despaginationPopover') despaginationPopover: IonPopover;
  despaginationPopoverOpened = false;

  @Input() index;
  @Input() file;
  @Input() reduced!: boolean;
  @Input() pFile: _PrintFile;
  @Input() printingGroup: PrintingGroup;
  @Input() settingsChanged;
  @Input() isLoading;
  @Input() printProduct;
  @Input() settings;
  @Input() printProductId: string = null;
  @Input() preview: boolean = false;
  @Output() fileAction = new EventEmitter<any>();
  @Output() hideGroupedFilesEvent = new EventEmitter<boolean>();

  fileSettings;
  uploading = false;
  progress = 0;
  uploadedFile;
  isUploaded = false;
  processing = false;
  fileUploaded: _PrintFile;
  private uploadSubscription: Subscription;
  id;
  total;
  prePath: string;
  pdfImages = null;

  //_________________________
  statusProgress: number = 0; //between 0 and 1
  actualSubstatus: string = "";
  substatusMessage: string = "Subiendo archivo";
  failed: boolean = false;
  substatus = PrintFileSubStatus;

  //_________________________

  blankSheetsBefore = 0;
  blankSheetsAfter = 0;
  editAliasButton = false;
  hasToShowDespaginationToggle = false;
  hasToShowCoverSettings = true;

  hideGroupedFiles = false;

  readonly uploadAnimationOptions: AnimationOptions = {
    path: 'https://maxst.icons8.com/vue-static/landings/animated-icons/icons/upload/upload.json',
    autoplay: true,
    loop: true,
  };

  readonly convertingAnimationOptions: AnimationOptions = {
    path: 'https://maxst.icons8.com/vue-static/landings/animated-icons/icons/document/document.json',
    autoplay: true,
    loop: true,
  };

  readonly countingAnimationOptions: AnimationOptions = {
    path: 'https://maxst.icons8.com/vue-static/landings/animated-icons/icons/counter/counter.json',
    autoplay: true,
    loop: true,
  };

  readonly thumbnailAnimationOptions: AnimationOptions = {
    path: 'https://maxst.icons8.com/vue-static/landings/animated-icons/icons/eye/eye.json',
    autoplay: true,
    loop: true,
  };

  readonly failedAnimationOptions: AnimationOptions = {
    path: 'https://maxst.icons8.com/vue-static/landings/animated-icons/icons/cross/cross.json',
    autoplay: true,
    loop: true,
  };

  constructor(
    protected fileService: FileService,
    private uploadService: UploadService,
    private productService: ProductService,
    private utilsService: UtilsService,
    private modalController: ModalController,
    public cartService: CartService,
    public appService: AppService,
    public utils: UtilsService
  ) {
  }

  ngOnInit() {
    this.fileUploaded = this.pFile;
    //Si el archivo está subido
    //Se especifica un path diferente para los productos de los influencers
    this.prePath = this.printProductId
      ? `products/${this.printProductId}/uploads`
      : "uploads";

    if (this.pFile.id) {
      this.getFile(this.pFile.id);
    } else {
      this.uploadFile();
    }

    this.blankSheetsBefore = this.pFile?.blankSheetsBefore ?? 0;
    this.blankSheetsAfter = this.pFile?.blankSheetsAfter ?? 0;
    const _printSettings = cloneDeep(this.printingGroup.printSettings);
    if (!this.pFile.coverType) {
      this.pFile.coverType = this.fileService.getDefaultCoverType(this.pFile, _printSettings);
    }

  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.pFile || changes.printingGroup || changes.settingsChanged) {
      if (changes.pFile) {
        this.fileUploaded = this.pFile;
        if (this.fileUploaded.status === PrintFileStatus.NEW) {
          this.printingGroup.files[this.index].docColor = this.printingGroup.printSettings.color === 'color';
        }
      }
      if (changes.printingGroup || changes.settingsChanged) {
        this.fileSettings = this.mergeSettings(this.printingGroup);
        this.checkCoverTypePaperWeight();
        this.openDespaginationPopover();
      }
      this.hasToShowDespaginationToggle = !!this.pFile.coverLaminated && this.settings.pagesPerSheet.selected === this.appService.getSelectedIndexForOption(Settings.PAGES_PER_SHEET, { value: 1 })
        && !!this.printingGroup.printSettings.twoSided;
      this.hasToShowCoverSettings = !this.pFile.coverLaminated || this.printingGroup.printSettings.twoSided;
    }
  }

  ngAfterViewChecked() {
    this.appService.registerTooltip(this.editTooltip);
  }

  ngOnDestroy(): void {
    this.appService.unregisterTooltip(this.editTooltip);
  }

  //Para thumbnail debemos hacer override de docColor y coverColor
  mergeSettings(printingGroup: PrintingGroup) {
    const settings = cloneDeep(printingGroup.printSettings);
    const _file = printingGroup.files[this.index];
    if (_file) {
      settings.docColor = _file.docColor;
      settings.coverColor = _file.coverType ? _file.coverType.coverColor : _file.coverColor;
      settings.ringColor = _file.ringColor && !settings.grouped ? _file.ringColor : settings.ringColor;
      settings.hardCoverFront = _file.hardCoverFront && !settings.grouped ? _file.hardCoverFront : settings.hardCoverFront;
      settings.hardCoverBack = _file.hardCoverBack && !settings.grouped ? _file.hardCoverBack : settings.hardCoverBack;
      settings.coverLaminated = _file.coverLaminated;
    }
    return settings;
  }

  getFile(id) {
    this.uploadService
      .getUpload$(id)
      .pipe(untilDestroyed(this))
      .subscribe((fileUploaded: PrintFile) => {
        // //busca thumbnail existente con settings combinados
        fileUploaded.thumbFile =
          getThumbFile(fileUploaded, this.fileSettings, this.pFile) ??
          fileUploaded.thumbFile;
        if (fileUploaded) {
          let data;
          Object.assign(this.fileUploaded, fileUploaded);

          if (fileUploaded.status === PrintFileStatus.FINISHED) {
            this.fileUploaded.thumbFile = fileUploaded.thumbFile;
            data = {
              action: "updateThumb",
              fileUploaded,
            };
          }
          if (fileUploaded.status === PrintFileStatus.FAILED) {
            data = {
              action: "fail",
            };
          }

          Object.assign(this.pFile, fileUploaded);

          if (data) {
            this.fileAction.emit(data);
          }

        } else {
          console.log(
            " no hay file uploaded?, estas eliminando? ",
            fileUploaded
          );
        }
      });
  }

  //TODO Hacer funcion más genérica para que se puedan comprobar otras propiedades
  /**
   * Check if the selected covertype.paperWeight is available for the selected page size and update it if not
   */
  checkCoverTypePaperWeight() {
    const coverType = this.pFile.coverType;
    const paperWeight = coverType?.paperWeight;
    const printSettings = this.printingGroup.printSettings;
    const pageSizeOptions = this.settings[Settings.PAGE_SIZE].options;
    const selectedPageSizeIndex = pageSizeOptions.findIndex(opt => opt.value === printSettings[Settings.PAGE_SIZE]) + 1;

    if (coverType && paperWeight) {
      const isAvailable = this.appService.getPrintSettings().paperWeight.options.some(option => {
        return option.value === paperWeight && !option.unavailableFor?.some(unavailable => {
          return unavailable.id === Settings.PAGE_SIZE && unavailable.options.includes(selectedPageSizeIndex);
        });
      });

      if (!isAvailable) {
        this.pFile.coverType.paperWeight = printSettings.paperWeight;
      }
    }
  }

  get isGroupedBindingFirstItem() {
    return this.printingGroup.printSettings.finishing === Finishing.BINDING
      && this.printingGroup.printSettings.grouped && this.index === 0;
  }

  uploadFile() {
    this.processing = false;
    const fileId = this.uploadService.createId();

    this.fileUploaded.id = fileId;
    setTimeout(() => {
      this.fileAction.emit({ action: "new" });
    }, 100);

    this.uploading = true;
    this.isUploaded = false;
    const file = this.file.file;

    this.uploadSubscription = this.uploadService
      .uploadFileWithProgress(file, fileId, this.prePath)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (event) => {
          if (event.type === 'progress') {
            this.progress = event.progress;
            this.uploadedFile = (event.progress * file.size) / 100;
            this.statusProgress = (0.2 * event.progress) / 100;
            if (event.progress === 100) {
              this.isUploaded = true;
              this.uploading = false;
              this.processing = true;
              this.uploadedFile = null;
              this.substatusMessage = "Preparando Documento";
            }
          } else if (event.type === 'complete') {
            const _printSettings = cloneDeep(this.printingGroup.printSettings);
            _printSettings.coverColor = false;
            this.uploadService.processFile(fileId, _printSettings, this.prePath).catch();

            this.uploadService
              .getUpload$(fileId)
              .pipe(takeWhile(() => this.file && !this.file.isUploaded))
              .subscribe((uploadedFile: PrintFile) => {
                if (uploadedFile) {
                  if (
                    uploadedFile.status === PrintFileStatus.FINISHED ||
                    uploadedFile.status === PrintFileStatus.FAILED
                  ) {
                    this.processing = false;
                    this.file.isUploaded = true;
                    Object.assign(this.fileUploaded, uploadedFile);
                    this.failed = uploadedFile.status === PrintFileStatus.FAILED;
                    if (!this.failed) {
                      this.fileAction.emit({ action: "uploaded" });
                      this.getFile(this.fileUploaded.id);
                      this.fileAction.emit({
                        action: "thumbnail",
                        id: this.fileUploaded.id,
                      });
                      this.openDespaginationPopover();
                    } else {
                      this.fileAction.emit({ action: "fail" });
                    }
                    this.statusProgress = this.failed ? this.statusProgress : 1;
                  } else {
                    this.UpdateStatusBar(uploadedFile.substatus);
                  }

                  Object.assign(this.fileUploaded, uploadedFile);
                  Object.assign(this.pFile, uploadedFile);
                  this.fileAction.emit({ action: "updateStatus" });
                }
              });
          }
        },
        error: (error) => {
          this.failed = true;
          this.fileAction.emit({ action: "error" });
          console.error("Get Error:", error, this.file);
        },
      });
  }

  private UpdateStatusBar(actualsubStatus: string): void {
    this.actualSubstatus = actualsubStatus;
    switch (actualsubStatus) {
      case this.substatus.NONE:
      case this.substatus.UPLOADING:
        break;
      case this.substatus.CONVERTING:
        this.substatusMessage = "Contando Páginas"; // MN: users get confused
        this.statusProgress = 0.4;
        break;
      case this.substatus.COUNTING:
        this.substatusMessage = "Contando Páginas";
        this.statusProgress = 0.6;
        break;
      case this.substatus.THUMBNAIL:
        this.substatusMessage = "Creando Miniatura";
        this.statusProgress = 0.8;
        break;
    }
  }

  borrar() {
    //aquí si hay id

    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
    }
    this.fileAction.emit({
      action: "delete",
    });
  }

  duplicate() {
    this.fileAction.emit({
      action: "duplicate",
    });
  }

  //TODO CHECK Pasar a Servicio
  // JA 22/05/2021
  // No se esta usando por ahora ya que los precios se actualizan  en home page al cargar.
  // revisar si realmente no hace falta
  // setPrice(file, printProduct) {
  //   const settings = cloneDeep(printProduct.product.printingGroup.printSettings);
  //   const totalSheets = this.productService.getTotalSheets(file.pages, settings); // this is actually SIDES, not SHEETS
  //   settings.coverColor = this.pFile.coverColor;
  //   settings.docColor = this.pFile.docColor; // MN: FIX: se estaban cobrando todos los docs como docColor cuando se seleccionaba sólo 1
  //   this.total = this.productService.getFilePrice(totalSheets, settings);
  // }

  onCoverColor(event) {
    setTimeout(() => {
      this.fileAction.emit({
        action: "coverColor",
        payload: {
          value: this.pFile.coverColor,
        },
      });
    }, 100);
  }

  onDocColor(event) {
    setTimeout(() => {
      this.fileAction.emit({
        action: "docColor",
        payload: {
          value: this.pFile.docColor,
        },
      });
    }, 100);
  }

  onPreview() {
    const path = this.fileUploaded.printFile.path;
    const pages = this.fileUploaded.pages;
    this.utilsService.getPreview({
      path,
      pages,
      watermark: false,
      printSettings: this.printingGroup.printSettings,
    });
  }

  truncate(num, index = 2) {
    // cutting the num
    return +num.toString().slice(0, num.toString().indexOf(".") + (index + 1));
  }

  hasBlankSheets() {
    return !!this.pFile.blankSheetsAfter || !!this.pFile.blankSheetsBefore;
  }

  async onAddBlankSheets() {
    const modal = await this.modalController.create({
      component: BlankSheetsModalComponent,
      componentProps: {
        sheetsBefore: this.pFile.blankSheetsBefore || 0,
        sheetsAfter: this.pFile.blankSheetsAfter || 0,
      },
      cssClass: "auto-height",
    });
    await modal.present();

    modal.onDidDismiss().then((resp) => {
      const { data } = resp;
      if (data) {
        this.addBlankSheets(data.blankSheetsBefore, data.blankSheetsAfter);
      }
    });
  }

  addBlankSheets(sheetsBefore, sheetsAfter) {
    setTimeout(() => {
      this.fileAction.emit({
        action: "blankSheets",
        blankSheetsBefore: sheetsBefore,
        blankSheetsAfter: sheetsAfter,
      });
    }, 100);
  }

  changeBlankSheetsQty(event, position) {
    const qty = event.qty;
    position === 'before' ? this.blankSheetsBefore = qty : this.blankSheetsAfter = qty;
    this.addBlankSheets(this.blankSheetsBefore, this.blankSheetsAfter);
  }

  protected readonly fill = fill;
  protected readonly Settings = Settings;
  protected readonly PageSize = PageSize;

  changeDocColor(event, color = null) {
    const value = color === null ? event.detail.value : color;

    this.fileAction.emit({
      action: 'docOptions',
      setting: 'docColor',
      value: value === 'color'
    });
  }

  changeDocLaminationType(event: any) {
    this.pFile.laminationType = event.detail.value;
  }

  checkAlias(event, alias = null) {
    alias = alias === null ? event?.target?.value : alias;
    this.pFile.alias = alias.length ? alias : this.pFile.originalFile.name;
    this.cartService.saveCart(this.cartService.cart);
  }

  onEditFile(alias = null) {
    if (this.editAliasButton) {
      this.checkAlias(null, alias);
    }
  }

  onFocusFile() {
    this.editAliasButton = true;
  }

  onBlurFile() {
    this.editAliasButton = false;
  }

  focusInput() {
    this.aliasInput.nativeElement.focus();
  }

  async openIndividualSettings() {
    const modal = await this.modalController.create({
      component: IndividualSettingsComponent,
      componentProps: {
        pFile: this.pFile,
        printProduct: this.printProduct,
        printingGroup: this.printingGroup,
        fileSettings: this.fileSettings,
        appSettings: this.settings,
        index: this.index
      },
      cssClass: "auto-height popup-individual-settings border-radius-20",
    });
    await modal.present();

    modal.onDidDismiss().then((resp) => {
      const { data } = resp;
      if (data) {
        this.addBlankSheets(data.blankSheetsBefore, data.blankSheetsAfter);
        this.changeDocColor(null, data.docColor);
        this.checkAlias(null, data.alias);
        this.pFile.coverType = data.coverType;
        if (!this.printProduct.product.printingGroup.printSettings.grouped) {
          this.pFile.ringColor = data.ringColor;
          this.pFile.hardCoverFront = data.hardCoverFront;
          this.pFile.hardCoverBack = data.hardCoverBack;
          this.fileSettings.ringColor = this.pFile.ringColor;
          this.fileSettings.hardCoverFront = this.pFile.hardCoverFront;
          this.fileSettings.hardCoverBack = this.pFile.hardCoverBack;
        } else if (this.index === 0) {
          const ringIndex = this.settings.ringColor.options.findIndex(op => op.value.id === data.ringColor.id);
          const hardCoverFrontIndex = this.settings.hardCoverFront.options.findIndex(op => op.value.id === data.hardCoverFront.id);
          const hardCoverBackIndex = this.settings.hardCoverBack.options.findIndex(op => op.value.id === data.hardCoverBack.id);
          this.printProduct.product.printingGroup.printSettings.ringColor = data.ringColor;
          this.printProduct.product.printingGroup.printSettings.hardCoverFront = data.hardCoverFront;
          this.printProduct.product.printingGroup.printSettings.hardCoverBack = data.hardCoverBack;
          this.settings.ringColor.selected = ringIndex + 1;
          this.settings.hardCoverFront.selected = hardCoverFrontIndex + 1;
          this.settings.hardCoverBack.selected = hardCoverBackIndex + 1;
        }
        this.pFile.laminationType = data.laminationType;
        this.pFile.laminationWeight = data.laminationWeight;
        this.fileSettings.laminationType = this.pFile.laminationType;
        this.fileSettings.laminationWeight = this.pFile.laminationWeight;

        this.fileSettings = this.mergeSettings(this.printingGroup);
        this.productService.refreshPrintGroupPrice(this.printProduct.product, this.printProduct.product.printingGroup.files);
        if (this.file) {
          this.file.total = this.printProduct.product.printingGroup.files[this.index]?.total;
        }
        this.printProduct.price.amt = this.printProduct.qty * (this.printProduct.product.unitPrice.amt + this.printProduct.product.extra.price);
        this.cartService.refreshCartPrice();
        this.cartService.setCartBindingUses();
        this.cartService.saveCart(this.cartService.cart);

      }
    });
  }

  async openIndividualBindingSettings() {
    const modal = await this.modalController.create({
      component: IndividualBindingOptionsModalComponent,
      componentProps: {
        settings: this.settings,
        printProduct: this.printProduct,
        file: this.pFile,
        isModal: true,
      },
      cssClass: "auto-height modal-width-400",
    });
    await modal.present();

    modal.onDidDismiss().then((resp) => {
      const { data } = resp;
      if (data) {
        const ringColorValue = data.settings.ringColor.options[data.settings.ringColor.selected - 1].value;
        const hardCoverFrontValue = data.settings.hardCoverFront.options[data.settings.hardCoverFront.selected - 1].value;
        const hardCoverBackValue = data.settings.hardCoverBack.options[data.settings.hardCoverBack.selected - 1].value;
        const laminationTypeValue = data.settings.laminationType.options[data.settings.laminationType.selected - 1].value;
        const laminationWeightValue = data.settings.laminationWeight.options[data.settings.laminationWeight.selected - 1].value;
        if (!this.printProduct.product.printingGroup.printSettings.grouped) {
          this.pFile.ringColor = ringColorValue;
          this.pFile.hardCoverFront = hardCoverFrontValue;
          this.pFile.hardCoverBack = hardCoverBackValue;
          this.fileSettings.ringColor = this.pFile.ringColor;
          this.fileSettings.hardCoverFront = this.pFile.hardCoverFront;
          this.fileSettings.hardCoverBack = this.pFile.hardCoverBack;
        } else {
          this.printProduct.product.printingGroup.printSettings.ringColor = ringColorValue;
          this.printProduct.product.printingGroup.printSettings.hardCoverFront = hardCoverFrontValue;
          this.printProduct.product.printingGroup.printSettings.hardCoverBack = hardCoverBackValue;
          this.printProduct.product.printingGroup.printSettings.laminationType = laminationTypeValue;
          this.printProduct.product.printingGroup.printSettings.laminationWeight = laminationWeightValue;
          this.settings.ringColor = data.settings.ringColor;
          this.settings.hardCoverFront = data.settings.hardCoverFront;
          this.settings.hardCoverBack = data.settings.hardCoverBack;
          this.settings.laminationType = data.settings.laminationType;
          this.settings.laminationWeight = data.settings.laminationWeight;
        }
        this.pFile.laminationType = laminationTypeValue;
        this.pFile.laminationWeight = laminationWeightValue;
        this.fileSettings.laminationType = this.pFile.laminationType;
        this.fileSettings.laminationWeight = this.pFile.laminationWeight;

        if (this.printingGroup.printSettings.finishing === Finishing.BINDING_LAMINATED) {
          for (let f of this.printingGroup.files) {
            f.laminationType = this.pFile.laminationType;
            f.laminationWeight = this.pFile.laminationWeight;
          }
        }

        this.fileSettings = this.mergeSettings(this.printingGroup);
        this.productService.refreshPrintGroupPrice(this.printProduct.product, this.printProduct.product.printingGroup.files);
        if (this.file) {
          this.file.total = this.printProduct.product.printingGroup.files[this.index]?.total;
        }
        this.printProduct.price.amt = this.printProduct.qty * (this.printProduct.product.unitPrice.amt + this.printProduct.product.extra.price);
        this.cartService.refreshCartPrice();
        this.cartService.setCartBindingUses();
        this.cartService.saveCart(this.cartService.cart);
      }
    });
  }

  getBindingSrc(part) {
    const orientation = this.fileSettings.pageOrientation === PageOrientation.PORTRAIT ? 'vertical' : 'horizontal';
    const side = this.fileSettings.pageTurn === PageTurn.LARGESIDE ? 'longside' : 'shortside';
    return `assets/images/thumbnail-finishings/binding-${part}-${side}-${orientation}.svg`;
  }

  async openCoverSettings() {
    if (this.hasToShowDespaginationToggle) {
      return;
    }
    const modal = await this.modalController.create({
      component: CoverSettingsComponent,
      componentProps: {
        fileSettings: this.fileSettings,
        currentCoverType: this.pFile.coverType
      },
      cssClass: "auto-height",
    });
    await modal.present();

    modal.onDidDismiss().then((resp) => {
      const { data } = resp;
      if (data?.coverType) {
        this.pFile.coverType = data.coverType;
        this.fileService.coverTypeChangedEvent.emit(this.index);
      }
    });
  }

  async openLaminationSettings() {
    const modal = await this.modalController.create({
      component: LaminationOptionsComponent,
      componentProps: {
        file: this.pFile,
        showButtons: true,
        settings: this.settings,
        laminationWeight: this.pFile.laminationWeight,
        laminationType: this.pFile.laminationType,
      },
      cssClass: "auto-height modal-width-400",
    });
    await modal.present();

    modal.onDidDismiss().then((resp) => {
      const { data } = resp;
      if (data?.laminationWeight) {
        this.pFile.laminationWeight = data.laminationWeight;
        this.pFile.laminationType = data.laminationType;
        this.fileService.coverTypeChangedEvent.emit(this.index);

      }
    });
  }

  get currentCoverTypeData() {
    return this.fileService.getCoverSettings().find(cs => cs.value.id === this.pFile.coverType?.id);
  }

  refreshFilePrice() {
    this.fileAction.emit({
      action: "refreshPrice",
    });
  }

  async openDespaginationInfoModal() {
    const modal = await this.modalController.create({
      component: InfoDespaginationModalComponent,
      cssClass: "auto-height",
      componentProps: {
        fileSettings: this.fileSettings,
        currentCoverType: this.pFile.coverType,
        docColor: this.pFile.docColor,
      },
    });
    modal.present();
  }

  changeDespagination(event: any) {
    this.pFile.coverType.twoSided = !event.detail.checked;
    this.pFile.coverType.id = 'Color' + (this.pFile.coverType.twoSided ? '2Side' : '1Side') + '1Page';
    if (this.fileService.getDefaultCoverType(this.fileSettings, this.fileSettings).id === this.pFile.coverType.id) {
      this.pFile.coverType.default = true;
    } else {
      delete this.pFile.coverType.default;
    }
  }

  async openDespaginationPopover() {
    setTimeout(async () => {
      if (!this.despaginationPopoverOpened && this.pFile.coverLaminated
        && this.settings.pagesPerSheet.selected === this.appService.getSelectedIndexForOption(Settings.PAGES_PER_SHEET, { value: 1 })) {
        if (this.despaginationIcon) {
          // Creamos un evento "fake" que simula un clic en el icono
          const iconElement = this.despaginationIcon.el; // 'el' es la propiedad que contiene el elemento nativo
          // Le pasamos el evento al popover para que se posicione correctamente
          this.despaginationPopover.event = {
            target: iconElement,
            preventDefault: () => {
            }, // Es necesario incluir estas propiedades para que funcione
            stopPropagation: () => {
            }
          };

          // Finalmente, presentamos el popover
          await this.despaginationPopover.present();
          this.despaginationPopoverOpened = true;
          setTimeout(() => {
            this.despaginationPopover.dismiss();
          }, 3000);
        }
      }
    }, 500);

  }

  public cancelUpload() {
    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
      this.uploadSubscription = null;
    }
  }

  toggleHideGroupedFiles() {
    this.hideGroupedFiles = !this.hideGroupedFiles;
    this.hideGroupedFilesEvent.emit(this.hideGroupedFiles);
  }
}

